import React, { lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { OrderProvider } from './context/OrderContext';
import { SalesProvider } from './context/SalesContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './layout/Layout';
import Login from './pages/Login';
import PerformanceMonitor from './components/PerformanceMonitor';
import { SalesLayout, SalesDashboard, SalesMenu, SalesOrders, SalesEditMenu, SalesRevenue } from './pages/Sales';
import { OrderReceiverLayout, OrderReceiverDashboard, OrderReceiverOrders, OrderReceiverRevenue } from './pages/OrderReceiver';

// Lazy load components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardFallback = lazy(() => import('./pages/DashboardFallback'));
const DashboardQuickFix = lazy(() => import('./pages/DashboardQuickFix'));
const DashboardTest = lazy(() => import('./pages/DashboardTest'));
const User = lazy(() => import('./pages/User'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Branches = lazy(() => import('./pages/Branches'));
const Production = lazy(() => import('./pages/Production'));
const Operations = lazy(() => import('./pages/Operations'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Costing = lazy(() => import('./pages/Costing'));
// const Procurements = lazy(() => import('./pages/Procurements')); // Removed as per user request
const Reports = lazy(() => import('./pages/Reports'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Supplier = lazy(() => import('./pages/Supplier'));
const ProcurementItemsCreation = lazy(() => import('./pages/ProcurementItemsCreation'));
const ProcurementPackaging = lazy(() => import('./pages/ProcurementPackaging'));
const ProcurementReview = lazy(() => import('./pages/ProcurementReview'));
const ProcurementCategory = lazy(() => import('./pages/ProcurementCategory'));
const ProcurementItems = lazy(() => import('./pages/ProcurementItems'));
const ProcurementItemDetails = lazy(() => import('./pages/ProcurementItemDetails'));
const ProcurementNewOrders = lazy(() => import('./pages/ProcurementNewOrders'));
const ProcurementOrders = lazy(() => import('./pages/ProcurementOrders'));
const ProcurementOrderReview = lazy(() => import('./pages/ProcurementOrderReview'));
const DishPricing = lazy(() => import('./pages/DishPricing'));
const ProcurementDishes = lazy(() => import('./pages/ProcurementDishes'));
// Removed Create section components: Suppliers, Packaging, RecipeExperts, Items
const Units = lazy(() => import('./pages/Units'));
const Roles = lazy(() => import('./pages/Roles'));
const Brand = lazy(() => import('./pages/Brand'));
const Currency = lazy(() => import('./pages/Currency'));
const MenuItems = lazy(() => import('./pages/MenuItems'));
const Tax = lazy(() => import('./pages/Tax'));
const EditItem = lazy(() => import('./pages/EditItem'));
// Removed UserManagement route and menu entry
const SubCategoryItems = lazy(() => import('./pages/SubCategoryItems'));
const SubCategories = lazy(() => import('./pages/SubCategories'));
const BranchOrders = lazy(() => import('./pages/BranchOrders'));
const Sections = lazy(() => import('./pages/Sections'));

// Import new category pages
const PurchaseCategory = lazy(() => import('./pages/PurchaseCategory'));
// Removed MenuCategory route and menu entry

// Import new Branch Order pages
const Branch = lazy(() => import('./pages/BranchOrder/Branch'));
const BranchOrderCentralKitchen = lazy(() => import('./pages/BranchOrder/CentralKitchen'));
const BranchOrderReports = lazy(() => import('./pages/BranchOrder/Reports'));
const BranchOrderSettings = lazy(() => import('./pages/BranchOrder/Settings'));
const CreateOrder = lazy(() => import('./pages/BranchOrder/CreateOrder'));
const OrderSubmission = lazy(() => import('./pages/BranchOrder/OrderSubmission'));
const OrderHistory = lazy(() => import('./pages/BranchOrder/OrderHistory'));
// const OrderHistoryTracking = lazy(() => import('./pages/BranchOrder/OrderHistoryTracking'));
const InventoryControl = lazy(() => import('./pages/BranchOrder/InventoryControl'));
const ReceivingOrder = lazy(() => import('./pages/BranchOrder/ReceivingOrder'));
const StockCount = lazy(() => import('./pages/BranchOrder/StockCount'));
const Wastage = lazy(() => import('./pages/BranchOrder/Wastage'));

// Import Central Kitchen pages
const CentralKitchenDashboard = lazy(() => import('./pages/CentralKitchen/Dashboard'));
const BranchOrdersHistory = lazy(() => import('./pages/CentralKitchen/BranchOrdersHistory'));
const PickList = lazy(() => import('./pages/CentralKitchen/PickList'));
const OrderProcessingPrep = lazy(() => import('./pages/CentralKitchen/OrderProcessingPrep'));

// Import new Branch Settings pages
const CreateItem = lazy(() => import('./pages/BranchSettings/CreateItem'));
const CreateCategory = lazy(() => import('./pages/BranchSettings/CreateCategory'));

// Import KCT pages
const KCTSection = lazy(() => import('./pages/KCT/Setup/Section'));
const KCTChefName = lazy(() => import('./pages/KCT/Setup/ChefName'));
const KCTUnit = lazy(() => import('./pages/KCT/Setup/Unit'));
const KCTItemCategory = lazy(() => import('./pages/KCT/Setup/ItemCategory'));
const KCTItemName = lazy(() => import('./pages/KCT/Setup/ItemName'));
const KCTSetupTracking = lazy(() => import('./pages/KCT/Setup/SetupTracking'));
const KCTLogConsumption = lazy(() => import('./pages/KCT/Chef/LogConsumption'));
const KCTConsumptionHistory = lazy(() => import('./pages/KCT/Reports/ConsumptionHistory'));

// Loading component for Suspense
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  // Memoize menuItems to prevent recreation on every render
  const menuItems = useMemo(() => [
    {
      label: 'Bike Sales',
      children: [
        { label: 'Dashboard', path: '/sales/dashboard' },
        { label: 'New Order', path: '/sales/menu' },
        { label: 'Orders', path: '/sales/orders' },
        { label: 'Revenue', path: '/sales/revenue' },
        { label: 'Edit Menu', path: '/sales/edit-menu' },
      ],
    },
    {
      label: 'Order Receiver',
      children: [
        { label: 'Dashboard', path: '/order-receiver/dashboard' },
        { label: 'Orders', path: '/order-receiver/orders' },
        { label: 'Revenue', path: '/order-receiver/revenue' },
      ],
    },
    {
      label: 'Branch Order',
      children: [
        { 
          label: 'Branch', 
          children: [
            { label: 'Create Order', path: '/branch-order/create-order' },
            { label: 'Order Submission', path: '/branch-order/order-submission' },
            { label: 'Order History & Tracking', path: '/branch-order/order-history' },
            { 
              label: 'Inventory Control', 
              children: [
                { label: 'Receiving Order', path: '/branch-order/receiving-order' },
                { label: 'Stock Count', path: '/branch-order/stock-count' },
                { label: 'Wastage', path: '/branch-order/wastage' },
              ]
            },
          ]
        },
        { 
          label: 'Central Kitchen', 
          children: [
            { label: 'Dashboard', path: '/central-kitchen/dashboard' },
            { label: 'Branch Orders & History', path: '/central-kitchen/branch-orders-history' },
            { label: 'Pick List', path: '/central-kitchen/pick-list' },
            { label: 'Order Processing & Prep', path: '/central-kitchen/order-processing-prep' },
          ]
        },
        { 
          label: 'Reports & Analytics', 
          children: [
            { label: 'Order Patterns', path: '/reports/order-patterns' },
            { label: 'Missing Item Analysis', path: '/reports/missing-item-analysis' },
            { label: 'Wastage Reports', path: '/reports/wastage-reports' },
          ]
        },
        { 
          label: 'Branch Settings', 
          children: [
            { label: 'Create Item', path: '/branch-settings/create-item' },
            { label: 'Branch Category', path: '/branch-settings/create-category' },
          ]
        },
      ],
    },
    {
      label: 'Item Category',
      children: [
      ],
    },
    // Removed Create section
     {
      label: 'Procurement',
      children: [
        { label: 'Supplier', path: '/procurements/settings/supplier' },
        { label: 'Category', path: '/procurements/settings/procurement-category' },
{ label: 'Create Item', path: '/procurements/creation/items-creation' },
            { label: 'Item Detail', path: '/procurements/items' },
        { label: 'New Orders', path: '/procurements/creation/new-orders' },
      ],
    },
    {
      label: 'Recipe',
      children: [
        { label: 'Dish Pricing', path: '/procurements/dish-pricing' },
        { label: 'Dishes List', path: '/procurements/dishes' },
      ]
    },
    {
      label: 'KCT',
      sublabel: 'Kitchen Consumption Tracking',
      children: [
        {
          label: 'Setup',
          children: [
            { label: '1. Section', path: '/kct/setup/section' },
            { label: '2. Chef Name', path: '/kct/setup/chef-name' },
            { label: '3. Unit', path: '/kct/setup/unit' },
            { label: '4. Item Category', path: '/kct/setup/item-category' },
            { label: '5. Item Name', path: '/kct/setup/item-name' },
            { label: '6. Setup Tracking', path: '/kct/setup/setup-tracking' },
          ]
        },
        {
          label: 'Chef',
          children: [
            { label: 'Log Consumption', path: '/kct/chef/log-consumption' },
          ]
        },
        {
          label: 'Reports',
          children: [
            { label: 'Consumption History', path: '/kct/reports/consumption-history' },
          ]
        },
      ],
    },
    {
      label: 'Settings',
      children: [
        { label: 'User', path: '/user' },
        { label: 'Gallery', path: '/gallery' },
        { label: 'Roles', path: '/roles' },
        { label: 'Units', path: '/units' },
        { label: 'Tax', path: '/tax' },
        { label: 'Branches', path: '/branches' },
        { label: 'Brand', path: '/brand' },
        { label: 'Currency', path: '/currency' },
        { label: 'Sections', path: '/sections' },
      ],
    },
    {
      label: 'System Status',
      path: '/dashboard',
    },
  ], []); // Empty dependency array since menuItems is static

  return (
    <AuthProvider>
      <OrderProvider>
        <Router>
      <Routes>
        {/* Public Route */}
        <Route path="/" element={<Login />} />

        {/* Protected Routes with Layout */}
        <Route
          path="/*"
          element={
              <ProtectedRoute>
                <RoleBasedRedirect>
            <Layout menuItems={menuItems}>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/sales" element={<SalesProvider><SalesLayout /></SalesProvider>}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<SalesDashboard />} />
                  <Route path="menu" element={<SalesMenu />} />
                  <Route path="orders" element={<SalesOrders />} />
                  <Route path="revenue" element={<SalesRevenue />} />
                  <Route path="edit-menu" element={<SalesEditMenu />} />
                </Route>
                  <Route path="/order-receiver" element={<OrderReceiverLayout />}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<OrderReceiverDashboard />} />
                  <Route path="orders" element={<OrderReceiverOrders />} />
                  <Route path="revenue" element={<OrderReceiverRevenue />} />
                </Route>
                  <Route path="/dashboard" element={
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  } />
                  <Route path="/dashboard-original" element={
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  } />
                  <Route path="/dashboard-fallback" element={<DashboardFallback />} />
                  <Route path="/dashboard-quick-fix" element={<DashboardQuickFix />} />
                  <Route path="/dashboard-test" element={<DashboardTest />} />
                  {/* /user-management route removed */}
                  <Route path="/user" element={<User />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/roles" element={<Roles />} />
                  <Route path="/units" element={<Units />} />
                  {/* Create section routes removed: /items, /recipe-experts, /suppliers, /packaging */}
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/reports" element={<Reports />} />
                  {/* Procurement Routes */}
                  <Route path="/procurements/settings/supplier" element={<Supplier />} />
                  <Route path="/procurements/settings/procurement-category" element={<ProcurementCategory />} />
                  <Route path="/procurements/creation/items-creation" element={<ProcurementItemsCreation />} />
                  <Route path="/procurements/creation/packaging" element={<ProcurementPackaging />} />
                  <Route path="/procurements/creation/review" element={<ProcurementReview />} />
                  <Route path="/procurements/creation/new-orders" element={<ProcurementNewOrders />} />
                  <Route path="/procurements/orders" element={<ProcurementOrders />} />
                  <Route path="/procurements/orders/:id/review" element={<ProcurementOrderReview />} />
                  <Route path="/procurements/items" element={<ProcurementItems />} />
                  <Route path="/procurements/items/:id/details" element={<ProcurementItemDetails />} />
                  <Route path="/procurements/dishes" element={<ProcurementDishes />} />
                  <Route path="/procurements/dish-pricing" element={<DishPricing />} />
                  <Route path="/costing" element={<Costing />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/operations" element={<Operations />} />
                  <Route path="/production" element={<Production />} />
                  <Route path="/branches" element={<Branches />} />
                  <Route path="/brand" element={<Brand />} />
                  <Route path="/currency" element={<Currency />} />
                  <Route path="/menu" element={<MenuItems />} />
                  <Route path="/tax" element={<Tax />} />
                  <Route path="/items/:id/edit" element={<EditItem />} />
                  <Route path="/sub-category-items" element={<SubCategoryItems />} />
                  <Route path="/sub-categories" element={<SubCategories />} />
                  <Route path="/branch-orders" element={<BranchOrders />} />
                  <Route path="/sections" element={<Sections />} />
                  
                  {/* New Category Routes */}
                  <Route path="/purchase-category" element={<PurchaseCategory />} />
                  {/* /menu-category route removed */}
                  {/* /branch-category route removed */}
                  
                  {/* Branch Order Routes */}
                  <Route path="/branch-order/branch" element={<Branch />} />
                  <Route path="/branch-order/central-kitchen" element={<BranchOrderCentralKitchen />} />
                  <Route path="/branch-order/reports" element={<BranchOrderReports />} />
                  <Route path="/branch-order/settings" element={<BranchOrderSettings />} />
                  <Route path="/branch-order/create-order" element={<CreateOrder />} />
                  <Route path="/branch-order/order-submission" element={<OrderSubmission />} />
                  <Route path="/branch-order/order-history" element={<OrderHistory />} />
                  <Route path="/branch-order/inventory-control" element={<InventoryControl />} />
                  <Route path="/branch-order/receiving-order" element={<ReceivingOrder />} />
                  <Route path="/branch-order/stock-count" element={<StockCount />} />
                  <Route path="/branch-order/wastage" element={<Wastage />} />
                  
                  {/* Central Kitchen Routes */}
                  <Route path="/central-kitchen/dashboard" element={<CentralKitchenDashboard />} />
                  <Route path="/central-kitchen/branch-orders-history" element={<BranchOrdersHistory />} />
                  <Route path="/central-kitchen/pick-list" element={<PickList />} />
                  <Route path="/central-kitchen/order-processing-prep" element={<OrderProcessingPrep />} />
                  
                  {/* Reports & Analytics Routes */}
                  <Route path="/reports/order-patterns" element={<div className="p-6"><h1>Order Patterns</h1><p>Coming Soon...</p></div>} />
                  <Route path="/reports/missing-item-analysis" element={<div className="p-6"><h1>Missing Item Analysis</h1><p>Coming Soon...</p></div>} />
                  <Route path="/reports/wastage-reports" element={<div className="p-6"><h1>Wastage Reports</h1><p>Coming Soon...</p></div>} />
                  
                                   {/* Branch Settings Routes */}
                   <Route path="/branch-settings/create-item" element={<CreateItem />} />
                   <Route path="/branch-settings/create-category" element={<CreateCategory />} />
                   
                   {/* KCT Routes */}
                   <Route path="/kct/setup/section" element={<KCTSection />} />
                   <Route path="/kct/setup/chef-name" element={<KCTChefName />} />
                   <Route path="/kct/setup/unit" element={<KCTUnit />} />
                   <Route path="/kct/setup/item-category" element={<KCTItemCategory />} />
                   <Route path="/kct/setup/item-name" element={<KCTItemName />} />
                   <Route path="/kct/setup/setup-tracking" element={<KCTSetupTracking />} />
                   <Route path="/kct/chef/log-consumption" element={<KCTLogConsumption />} />
                   <Route path="/kct/reports/consumption-history" element={<KCTConsumptionHistory />} />
                </Routes>
              </Suspense>
            </Layout>
                </RoleBasedRedirect>
              </ProtectedRoute>
          }
        />
      </Routes>
        </Router>
        <PerformanceMonitor />
      </OrderProvider>
    </AuthProvider>
  );
}

export default App;